import { runner } from '@studio/js-utils/async';
import { isPendingDevice, isTransient, toDeviceId } from '@studio/models/device';
import { broker, loadSnapshot, resetRestoreEpoch, restoreAgent } from '#~/agent/broker.js';
import { ElectronAPI } from '#~/electron/api.js';
import { getBrands, getTemplate } from '#~/device/registry/commands/index.js';
import { pbToJsDevice } from '#~/protobuf/index.js';
import { LiveKitTokenCreate } from '@tryiris-ai/mailroom-helper/livekit';

type RestoreTarget = Parameters<typeof restoreAgent>[0];
// A restore epoch belongs to a proxy identity; repeated probes must reuse that identity.
type RestoreView = {
  view: RestoreTarget;
  record?: (args: Parameters<RestoreTarget['mutate']>, write: Promise<void>) => void;
};
const restoreViews = new WeakMap<RestoreTarget, RestoreView>();

const tokenRequests = new Map<string, number>();
let observingTokens = false;

/** Observe real token requests before boot; installed only by the E2E-gated device probe. */
export function observeRestoreTokenRequests(): void {
  if (observingTokens) return;
  observingTokens = true;
  const request = LiveKitTokenCreate.request;
  LiveKitTokenCreate.request = async function (...args) {
    const identity = args[1].identity;
    if (typeof identity !== 'string') return request.apply(this, args);
    tokenRequests.set(identity, (tokenRequests.get(identity) ?? 0) + 1);
    try {
      return await request.apply(this, args);
    } finally {
      // Count callers, including coalesced requests, until the real transport settles.
      const remaining = (tokenRequests.get(identity) ?? 1) - 1;
      if (remaining === 0) tokenRequests.delete(identity);
      else tokenRequests.set(identity, remaining);
    }
  };
}

const tokenPending = (deviceId: string): boolean => (tokenRequests.get(deviceId) ?? 0) > 0;

/** Settle boot's saved-state and metadata work before measuring a new restore epoch. */
export async function prepareRestoreEpoch(deviceId: string): Promise<void> {
  const target = [...broker].find(
    (candidate) =>
      candidate.embedded === false && candidate.state.control?.device?.[deviceId] !== undefined
  );
  if (target === undefined || isTransient(toDeviceId(deviceId)))
    throw new Error('Restore receipt requires a persisted camera on a non-embedded bridge');
  const token = await ElectronAPI.getAccessToken();
  const snapshot = await loadSnapshot(target, token);
  if (snapshot?.control?.studio?.device?.[deviceId] === undefined)
    throw new Error('Restore receipt requires a persisted camera snapshot after relaunch');
  const device = pbToJsDevice(target.id, deviceId, snapshot, target.embedded);
  if (device?.modelId === undefined)
    throw new Error('Restore receipt requires the saved camera model');
  // Boot emits only after these shared registry requests finish. Waiting on
  // their existing mutex/cache prevents late boot metadata from crossing phases.
  await Promise.all([getTemplate(device.modelId, token), getBrands(token)]);
}

/** Drive the real restore function against a leased camera's saved state. */
export async function restoreEpochProbe(
  reset: boolean,
  deviceId: string
): Promise<{
  agentId: string;
  deviceId: string;
  mutations: number;
}> {
  // The test records the added camera's STag id before quitting. A snapshot can
  // start with a transient preview, which production restore deliberately skips.
  if (isTransient(toDeviceId(deviceId)))
    throw new Error('Restore receipt requires a persisted camera, not a transient preview');
  const target = [...broker].find(
    (candidate) =>
      candidate.embedded === false && candidate.state.control?.device?.[deviceId] !== undefined
  );
  if (target === undefined)
    throw new Error('Restore receipt requires a real camera on a non-embedded bridge');
  const token = await ElectronAPI.getAccessToken();
  const snapshot = await loadSnapshot(target, token);
  if (snapshot?.control?.studio?.device?.[deviceId] === undefined)
    throw new Error('Restore receipt requires a persisted camera snapshot after relaunch');
  if (tokenPending(deviceId) || isPendingDevice(toDeviceId(deviceId)))
    throw new Error('Restore receipt camera is still restoring');
  if (reset) resetRestoreEpoch(target.id);
  let mutations = 0;
  const writes: Promise<void>[] = [];
  // A reset loses the renderer's status before the restore callback runs. Keep
  // that exact read boundary empty while forwarding every mutation to the real
  // bridge. No tracking function or PostHog payload is replaced by this probe.
  let observed = restoreViews.get(target);
  if (!observed) {
    const owner: RestoreView = {
      view: new globalThis.Proxy(target, {
        get(current, key) {
          if (key === 'state')
            return { ...current.state, status: { ...current.state.status, device: {} } };
          if (key === 'mutate')
            return (...args: Parameters<typeof target.mutate>) => {
              const write = current.mutate(...args);
              owner.record?.(args, write);
              return write;
            };
          const value = Reflect.get(current, key, current);
          return typeof value === 'function' ? value.bind(current) : value;
        }
      })
    };
    observed = owner;
    restoreViews.set(target, owner);
  }
  if (observed.record) throw new Error('Restore receipt probe already running');
  observed.record = (args, write) => {
    if (args[0].control?.device?.[deviceId] !== undefined) mutations++;
    writes.push(write);
  };
  const view = observed.view;
  try {
    const result = await runner(() => restoreAgent(view, token, ElectronAPI.getAccessToken));
    if (result.completed !== true) throw new Error('Restore receipt callback did not complete');
    await Promise.all(writes);
  } finally {
    delete observed.record;
  }
  return { agentId: target.id, deviceId, mutations };
}

/** Wait on the same pending-device state that controls production restoration. */
export function restoreEpochReady(deviceId: string): boolean {
  if (
    isTransient(toDeviceId(deviceId)) ||
    tokenPending(deviceId) ||
    isPendingDevice(toDeviceId(deviceId))
  )
    return false;
  return [...broker].some(
    (proxy) =>
      proxy.embedded === false &&
      proxy.state.control?.device?.[deviceId] !== undefined &&
      proxy.state.status?.device?.[deviceId] !== undefined
  );
}
